import { type Ref, onMounted, onUnmounted, watch } from 'vue'

/**
 * Module-level state shared across all composable instances.
 * Reference counting ensures scroll stays locked when multiple components request it,
 * and only unlocks when ALL components have released their lock.
 */
let lockCount = 0

function lockBodyScroll(): void {
    if (lockCount === 0) {
        document.documentElement.classList.add('disable_scroll')
    }
    lockCount++
}

function unlockBodyScroll(): void {
    lockCount = Math.max(0, lockCount - 1)

    if (lockCount === 0) {
        document.documentElement.classList.remove('disable_scroll')
    }
}

function evaluateCondition(condition: Ref<boolean> | (() => boolean)): boolean {
    return typeof condition === 'function' ? condition() : condition.value
}

export function useBodyScrollLock(condition?: Ref<boolean> | (() => boolean)) {
    let isLocked = false

    function setScrollLock(value: boolean) {
        // Each instance owns at most one lock and can release only its own lock.
        if (value === isLocked) {
            return
        }
        isLocked = value
        if (value) {
            lockBodyScroll()
        } else {
            unlockBodyScroll()
        }
    }

    if (condition) {
        watch(condition, (shouldLock) => {
            setScrollLock(shouldLock)
        })

        onMounted(() => {
            if (evaluateCondition(condition)) {
                setScrollLock(true)
            }
        })
    }

    onUnmounted(() => {
        setScrollLock(false)
    })

    return { setScrollLock }
}
